
const libPictProvider = require('pict-provider');
const libCatalog = require('./DatalakeCatalog-Provider.js');

/**
 * Data Lake dataset provider — the per-dataset detail view (#/Datalake/Dataset/:GUID).
 *
 * Resolves the MaterializedProjection catalog row by GUID (main /1.0/ API), then reads the dataset's
 * column schema, a paginated page of rows, and its lineage from the PDO PrivateDataLake / DataCatalog
 * surface (same /1.0/ origin via Kong). Lake tables are flat (no relationships), so rows render as a
 * paginated table with columns derived from the JSON-Schema at runtime.
 *
 * Read-URL grammar (authoritative: PrivateLakeProvider._fetchProjectionBySegments): the logical name is
 * the PhysicalTableName with the customer-silo prefix stripped (C182_PD_MoistureDaily -> PD_MoistureDaily).
 * The resolver joins URL segments with '_' and matches ProjectionName EQ <suffix> OR PhysicalTableName LK
 * 'C%_<suffix>', so the single-segment logical always resolves. Schema/single-row use the singular name;
 * list/count pluralize it (append 's'). Lineage takes the PHYSICAL address: lake/<PhysicalTableName>.
 * Schema is loaded first so the lazy registration mounts routes off the singular segments.
 */

const PAGE_SIZE = 50;
const MAX_VISIBLE_COLUMNS = 16;

/** Stringify a lake cell value for the table (objects -> compact JSON, long values truncated). */
function cellText(pValue)
{
	if (pValue === null || pValue === undefined)
	{
		return '';
	}
	let tmpText;
	if (typeof(pValue) === 'object')
	{
		try { tmpText = JSON.stringify(pValue); }
		catch (pError) { tmpText = String(pValue); }
	}
	else
	{
		tmpText = String(pValue);
	}
	return (tmpText.length > 120) ? `${tmpText.slice(0, 117)}…` : tmpText;
}

/** Build the visible column descriptors from a JSON-Schema `properties` object. */
function columnsFromSchema(pProperties)
{
	const tmpProps = (pProperties && typeof(pProperties) === 'object') ? pProperties : {};
	const tmpKeys = Object.keys(tmpProps);
	return tmpKeys.slice(0, MAX_VISIBLE_COLUMNS).map((pKey) => ({ Key: pKey, Label: pKey, Type: (tmpProps[pKey] && tmpProps[pKey].type) || '' }));
}

class DatalakeDatasetProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	render()
	{
		if (this.pict.views.DatalakeDataset)
		{
			this.pict.views.DatalakeDataset.render();
		}
	}

	initializeState()
	{
		this.pict.AppData.DatalakeDataset =
		{
			Loading: false,
			ErrorSlot: [],
			Row: null,
			Kind: 'computed',
			IsImported: false,
			IsComputed: true,

			HeaderName: '',
			HeaderSub: '',
			ChipLabel: '',
			ChipClass: '',
			ChipIcon: '',
			MetaRows: [],

			SchemaRows: [],
			SchemaColumnCount: 0,

			Columns: [],
			Rows: [],
			RowCount: 0,
			Begin: 0,
			PageLabel: '',
			PrevDisabledAttr: 'disabled',
			NextDisabledAttr: 'disabled',
			ColumnsTruncatedNote: '',
			RowsLoadingSlot: [],
			RowsEmptySlot: [],

			LineageUpstream: [],
			LineageDownstream: [],
			LineageSlot: [],

			// Management (mutations): schedule edit + refresh + re-import.
			CanManageSchedule: false,
			CanReImport: false,
			ManageSlot: [],
			ComputedManageSlot: [],
			ImportedManageSlot: [],
			RawActionSlot: [],
			Schedule: { Enabled: false, Mode: 'interval', Cron: '', IntervalMinutes: 60, Group: '' },
			ScheduleEnabledAttr: '',
			ScheduleModeCronChecked: '',
			ScheduleModeIntervalChecked: 'checked',
			ScheduleCronClass: 'is-hidden',
			ScheduleIntervalClass: '',
			ActionBusy: false,
			ActionBusySlot: [],
			ActionResultSlot: [],
			RefreshDisabledAttr: '',

			LoadingSlot: [],
		};
	}

	/** The logical lake-read name (PhysicalTableName minus the C<n>_ silo prefix). */
	get logicalName()
	{
		const tmpRow = this.pict.AppData.DatalakeDataset.Row || {};
		return libCatalog.deriveLogicalReadName(tmpRow.PhysicalTableName);
	}

	/**
	 * Open a dataset by its MaterializedProjection GUID: resolve the catalog row, then load schema + rows +
	 * lineage. Re-reads the row (so a deep-link works without the catalog list being loaded).
	 * @param {string} pGUID
	 */
	openByGUIDAsync(pGUID)
	{
		// initializeState() REPLACES AppData.DatalakeDataset, so capture the reference AFTER it (else writes
		// land on an orphaned object while the view reads the fresh, empty one).
		this.initializeState();
		const tmpState = this.pict.AppData.DatalakeDataset;
		tmpState.Loading = true;
		tmpState.LoadingSlot = [ {} ];
		this.render();

		this.restClient.getJSON(`MaterializedProjections/FilteredTo/FBV~GUIDMaterializedProjection~EQ~${encodeURIComponent(pGUID)}/0/1`, (pError, pBody) =>
		{
			if (pError || !Array.isArray(pBody) || pBody.length < 1)
			{
				tmpState.Loading = false;
				tmpState.LoadingSlot = [];
				tmpState.ErrorSlot = [ { Message: pError ? `Could not load this dataset: ${pError.message || pError}` : 'Dataset not found.' } ];
				this.render();
				return;
			}
			tmpState.Row = pBody[0];
			tmpState.Kind = libCatalog.classifyKind(tmpState.Row);
			tmpState.IsImported = (tmpState.Kind === 'imported');
			tmpState.IsComputed = (tmpState.Kind === 'computed');
			tmpState.Begin = 0;
			this._deriveHeaderAndMeta();
			this._deriveActions();
			this.render();
			this._loadSchema();
			this._loadLineage();
		});
	}

	/**
	 * Open a RAW imported lake table by its physical name — a clone table (e.g. C182_LastradaDB_ASPHALT) that
	 * has no MaterializedProjection registry row. Synthesizes a read-only catalog row, then loads schema +
	 * rows + lineage the same way. No management actions (there is no projection definition to refresh).
	 * @param {string} pPhysicalTableName
	 */
	openByTableAsync(pPhysicalTableName)
	{
		this.initializeState();
		const tmpState = this.pict.AppData.DatalakeDataset;
		tmpState.Loading = true;
		tmpState.LoadingSlot = [ {} ];
		tmpState.Row =
		{
			PhysicalTableName: pPhysicalTableName,
			ProjectionName: libCatalog.deriveLogicalReadName(pPhysicalTableName),
			SourceType: 'PrivateLakeRaw',
			OperationType: '',
			SourceAddress: `lake/${pPhysicalTableName}`,
			TargetAddress: `lake/${pPhysicalTableName}`,
			RefreshEnabled: 0,
			Description: 'Imported raw lake table (no projection definition).',
			IsRaw: true,
		};
		tmpState.Kind = 'imported';
		tmpState.IsImported = true;
		tmpState.IsComputed = false;
		tmpState.Begin = 0;
		this._deriveHeaderAndMeta();
		this._deriveActions();
		this.render();
		this._loadSchema();
		this._loadLineage();
	}

	/** Load the dataset's column schema (singular name → triggers lazy registration), then count + rows. */
	_loadSchema()
	{
		const tmpState = this.pict.AppData.DatalakeDataset;
		const tmpLogical = this.logicalName;
		this.restClient.getJSON(`PrivateDataLake/${tmpLogical}/Schema`, (pError, pBody) =>
		{
			if (pError)
			{
				tmpState.Loading = false;
				tmpState.LoadingSlot = [];
				const tmpIsRaw = tmpState.Row && tmpState.Row.IsRaw;
				tmpState.ErrorSlot = [ { Message: tmpIsRaw
					? 'This is a raw imported source table — catalogued in your data lake, but not directly queryable over the API (only materialized projections are). Create a projection from it to browse and use its data.'
					: `Could not read the dataset schema: ${pError.message || pError}` } ];
				this.render();
				return;
			}
			const tmpProperties = (pBody && pBody.properties) || {};
			const tmpKeys = Object.keys(tmpProperties);
			tmpState.SchemaRows = tmpKeys.map((pKey) => ({ Column: pKey, Type: (tmpProperties[pKey] && tmpProperties[pKey].type) || '', Size: (tmpProperties[pKey] && tmpProperties[pKey].size) || '' }));
			tmpState.SchemaColumnCount = tmpKeys.length;
			tmpState.Columns = columnsFromSchema(tmpProperties);
			tmpState.ColumnsTruncatedNote = (tmpKeys.length > MAX_VISIBLE_COLUMNS)
				? `Showing the first ${MAX_VISIBLE_COLUMNS} of ${tmpKeys.length} columns.`
				: '';
			this.render();
			this._loadCount();
			this._loadRowsPage();
		});
	}

	_loadCount()
	{
		const tmpState = this.pict.AppData.DatalakeDataset;
		this.restClient.getJSON(`PrivateDataLake/${this.logicalName}s/Count`, (pError, pBody) =>
		{
			if (!pError && pBody)
			{
				tmpState.RowCount = (typeof(pBody.Count) === 'number') ? pBody.Count : (parseInt(pBody.Count, 10) || 0);
				this._derivePager();
				this.render();
			}
		});
	}

	_loadRowsPage()
	{
		const tmpState = this.pict.AppData.DatalakeDataset;
		tmpState.RowsLoadingSlot = [ {} ];
		tmpState.RowsEmptySlot = [];
		this.render();
		this.restClient.getJSON(`PrivateDataLake/${this.logicalName}s/${tmpState.Begin}/${PAGE_SIZE}`, (pError, pBody) =>
		{
			tmpState.Loading = false;
			tmpState.LoadingSlot = [];
			tmpState.RowsLoadingSlot = [];
			if (pError)
			{
				tmpState.ErrorSlot = [ { Message: `Could not read dataset rows: ${pError.message || pError}` } ];
				this.render();
				return;
			}
			const tmpRecords = Array.isArray(pBody) ? pBody : [];
			tmpState.Rows = tmpRecords.map((pRecord) => ({
				Cells: tmpState.Columns.map((pColumn) => ({ Value: cellText(pRecord[pColumn.Key]) })),
			}));
			tmpState.RowsEmptySlot = (tmpRecords.length === 0) ? [ {} ] : [];
			this._derivePager();
			this.render();
		});
	}

	_derivePager()
	{
		const tmpState = this.pict.AppData.DatalakeDataset;
		const tmpFrom = tmpState.RowCount === 0 ? 0 : tmpState.Begin + 1;
		const tmpTo = Math.min(tmpState.Begin + PAGE_SIZE, tmpState.RowCount || (tmpState.Begin + tmpState.Rows.length));
		tmpState.PageLabel = `${tmpFrom.toLocaleString('en-US')}–${tmpTo.toLocaleString('en-US')} of ${(tmpState.RowCount || 0).toLocaleString('en-US')}`;
		tmpState.PrevDisabledAttr = (tmpState.Begin <= 0) ? 'disabled' : '';
		tmpState.NextDisabledAttr = ((tmpState.Begin + PAGE_SIZE) >= tmpState.RowCount) ? 'disabled' : '';
	}

	_loadLineage()
	{
		const tmpState = this.pict.AppData.DatalakeDataset;
		const tmpRow = tmpState.Row || {};
		const tmpAddress = tmpRow.TargetAddress || `lake/${tmpRow.PhysicalTableName}`;
		this.restClient.getJSON(`DataCatalog/Lineage?address=${encodeURIComponent(tmpAddress)}`, (pError, pBody) =>
		{
			if (pError || !pBody)
			{
				return; // lineage is best-effort; never block the viewer on it
			}
			const tmpUpstream = pBody.Upstream || pBody.upstream || [];
			const tmpDownstream = pBody.Downstream || pBody.downstream || [];
			tmpState.LineageUpstream = (Array.isArray(tmpUpstream) ? tmpUpstream : []).map((pItem) => ({ Address: (typeof(pItem) === 'string') ? pItem : (pItem.Address || pItem.address || JSON.stringify(pItem)) }));
			tmpState.LineageDownstream = (Array.isArray(tmpDownstream) ? tmpDownstream : []).map((pItem) => ({ Address: (typeof(pItem) === 'string') ? pItem : (pItem.Address || pItem.address || JSON.stringify(pItem)) }));
			tmpState.LineageSlot = (tmpState.LineageUpstream.length > 0 || tmpState.LineageDownstream.length > 0) ? [ {} ] : [];
			this.render();
		});
	}

	_deriveHeaderAndMeta()
	{
		const tmpState = this.pict.AppData.DatalakeDataset;
		const tmpRow = tmpState.Row || {};
		const tmpChip = libCatalog.deriveStatusChip(tmpRow);
		tmpState.ChipLabel = tmpChip.Label;
		tmpState.ChipClass = tmpChip.Class;
		tmpState.ChipIcon = tmpChip.Icon;
		tmpState.HeaderName = tmpRow.ProjectionName || libCatalog.deriveLogicalReadName(tmpRow.PhysicalTableName);
		tmpState.HeaderSub = tmpRow.Description || '';

		const tmpMeta = [];
		tmpMeta.push({ Label: 'Physical table', Value: tmpRow.PhysicalTableName || '' });
		tmpMeta.push({ Label: 'Kind', Value: tmpState.IsImported ? 'Imported clone (read-only)' : 'Computed projection' });
		tmpMeta.push({ Label: 'Operation', Value: tmpRow.OperationType || '' });
		tmpMeta.push({ Label: 'Source', Value: tmpRow.SourceAddress || tmpRow.SourceDescriptor || '' });
		tmpMeta.push({ Label: 'Target', Value: tmpRow.TargetAddress || '' });
		if (tmpState.IsComputed)
		{
			tmpMeta.push({ Label: 'Refresh', Value: libCatalog.deriveCadenceLabel(tmpRow) });
			if (tmpRow.RefreshGroup) { tmpMeta.push({ Label: 'Pipeline', Value: tmpRow.RefreshGroup }); }
			tmpMeta.push({ Label: 'Last run', Value: this._formatRunStamp(tmpRow) });
		}
		else
		{
			tmpMeta.push({ Label: 'Imported', Value: this._formatDate(tmpRow.LastRefreshDate || tmpRow.CreateDate) });
		}
		tmpState.MetaRows = tmpMeta;
	}

	_formatRunStamp(pRow)
	{
		const tmpStatus = pRow.LastRefreshStatus || '—';
		const tmpDate = this._formatDate(pRow.LastRefreshDate);
		return tmpDate ? `${tmpStatus} · ${tmpDate}` : tmpStatus;
	}

	_formatDate(pValue)
	{
		if (!pValue)
		{
			return '';
		}
		const tmpString = String(pValue);
		// Render the date portion only (avoid a Date() parse that varies by tz); the API returns ISO-ish.
		const tmpMatch = tmpString.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/);
		return tmpMatch ? `${tmpMatch[1]} ${tmpMatch[2]}` : tmpString.slice(0, 19);
	}

	// --- Management (mutations) ---

	/** Decide which management affordances apply + seed the schedule editor from the row. */
	_deriveActions()
	{
		const tmpState = this.pict.AppData.DatalakeDataset;
		const tmpRow = tmpState.Row || {};
		tmpState.CanManageSchedule = tmpState.IsComputed;
		tmpState.CanReImport = tmpState.IsImported && !tmpRow.IsRaw;
		tmpState.ManageSlot = (tmpState.CanManageSchedule || tmpState.CanReImport) ? [ {} ] : [];
		tmpState.ComputedManageSlot = tmpState.CanManageSchedule ? [ {} ] : [];
		tmpState.ImportedManageSlot = tmpState.CanReImport ? [ {} ] : [];
		// Raw imported table → offer to "projectionize" it into a queryable projection.
		tmpState.RawActionSlot = tmpRow.IsRaw ? [ {} ] : [];

		const tmpHasCron = !!(tmpRow.RefreshCron && String(tmpRow.RefreshCron).trim());
		tmpState.Schedule =
		{
			Enabled: !!tmpRow.RefreshEnabled,
			Mode: tmpHasCron ? 'cron' : 'interval',
			Cron: tmpRow.RefreshCron || '',
			IntervalMinutes: parseInt(tmpRow.RefreshIntervalMinutes, 10) || 60,
			Group: tmpRow.RefreshGroup || '',
		};
		this._deriveScheduleAttrs();
	}

	_deriveScheduleAttrs()
	{
		const tmpState = this.pict.AppData.DatalakeDataset;
		const tmpSchedule = tmpState.Schedule;
		tmpState.ScheduleEnabledAttr = tmpSchedule.Enabled ? 'checked' : '';
		tmpState.ScheduleModeCronChecked = (tmpSchedule.Mode === 'cron') ? 'checked' : '';
		tmpState.ScheduleModeIntervalChecked = (tmpSchedule.Mode === 'interval') ? 'checked' : '';
		tmpState.ScheduleCronClass = (tmpSchedule.Enabled && tmpSchedule.Mode === 'cron') ? '' : 'is-hidden';
		tmpState.ScheduleIntervalClass = (tmpSchedule.Enabled && tmpSchedule.Mode === 'interval') ? '' : 'is-hidden';
	}

	setScheduleEnabledLocal(pChecked)
	{
		this.pict.AppData.DatalakeDataset.Schedule.Enabled = !!pChecked;
		this._deriveScheduleAttrs();
		this.render();
	}

	setScheduleMode(pMode)
	{
		this.pict.AppData.DatalakeDataset.Schedule.Mode = (pMode === 'cron') ? 'cron' : 'interval';
		this._deriveScheduleAttrs();
		this.render();
	}

	/** Update a schedule field WITHOUT re-rendering (so text/number input cursors aren't reset). */
	setScheduleField(pField, pValue)
	{
		const tmpSchedule = this.pict.AppData.DatalakeDataset.Schedule;
		if (pField === 'cron') { tmpSchedule.Cron = pValue || ''; }
		else if (pField === 'interval') { tmpSchedule.IntervalMinutes = parseInt(pValue, 10) || 0; }
		else if (pField === 'group') { tmpSchedule.Group = pValue || ''; }
	}

	/** PUT the full schedule triplet so switching modes clears the stale field (see 05-api-reference). */
	saveSchedule()
	{
		const tmpState = this.pict.AppData.DatalakeDataset;
		const tmpRow = tmpState.Row || {};
		if (!tmpState.CanManageSchedule || tmpState.ActionBusy)
		{
			return;
		}
		const tmpSchedule = tmpState.Schedule;
		const tmpRecord =
		{
			IDMaterializedProjection: tmpRow.IDMaterializedProjection,
			RefreshEnabled: tmpSchedule.Enabled ? 1 : 0,
			RefreshCron: (tmpSchedule.Enabled && tmpSchedule.Mode === 'cron') ? String(tmpSchedule.Cron || '') : '',
			RefreshIntervalMinutes: (tmpSchedule.Enabled && tmpSchedule.Mode === 'interval') ? (parseInt(tmpSchedule.IntervalMinutes, 10) || 0) : 0,
			RefreshGroup: String(tmpSchedule.Group || ''),
		};
		this._setBusy(true);
		this.restClient.updateEntity('MaterializedProjection', tmpRecord, (pError) =>
		{
			this._setBusy(false);
			if (pError)
			{
				this._setActionResult('is-danger', 'fa-triangle-exclamation', `Could not update the schedule: ${pError.message || pError}`);
				return;
			}
			this._setActionResult('is-success', 'fa-circle-check', tmpSchedule.Enabled ? 'Schedule saved — automatic refreshes are enabled.' : 'Schedule paused.');
			this._reloadRowOnly();
		});
	}

	/** Re-run this projection now (POST /1.0/Projection rebuilt from the registry row). */
	refreshNow()
	{
		const tmpState = this.pict.AppData.DatalakeDataset;
		if (!tmpState.CanManageSchedule || tmpState.ActionBusy)
		{
			return;
		}
		this._runRerun('Refresh', 'Refreshing — this can take a while for large sources…');
	}

	/** Re-pull an imported clone from its source (durable source, safe to re-run). */
	reImport()
	{
		const tmpState = this.pict.AppData.DatalakeDataset;
		if (!tmpState.CanReImport || tmpState.ActionBusy)
		{
			return;
		}
		this._runRerun('Re-import', 'Re-importing from the source…');
	}

	_runRerun(pLabel, pBusyMessage)
	{
		const tmpState = this.pict.AppData.DatalakeDataset;
		const tmpBody = this._buildRerunBody(tmpState.Row);
		if (!tmpBody)
		{
			this._setActionResult('is-danger', 'fa-triangle-exclamation', `Could not reconstruct this dataset's definition to ${pLabel.toLowerCase()} it.`);
			return;
		}
		this._setBusy(true);
		this._setActionResult('is-info', 'fa-rotate', pBusyMessage);
		this.restClient.postJSON('Projection', tmpBody, (pError, pResponse) =>
		{
			this._setBusy(false);
			if (pError)
			{
				// Long runs can 504 at the gateway while continuing server-side — treat as indeterminate.
				this._setActionResult('is-warning', 'fa-clock', `${pLabel} was submitted but didn't confirm in time — it may still be running. Reloading status…`);
				this._reloadRowOnly();
				this._loadCount();
				return;
			}
			this._afterRunResult(pResponse, pLabel);
		});
	}

	/** Reconstruct the POST /1.0/Projection body from the stored registry row (OperationConfiguration from SourceConfigJSON). */
	_buildRerunBody(pRow)
	{
		if (!pRow || !pRow.OperationType || !pRow.SourceAddress || !pRow.TargetAddress)
		{
			return null;
		}
		const tmpBody =
		{
			IDCustomer: pRow.IDCustomer,
			Name: pRow.ProjectionName,
			OperationType: pRow.OperationType,
			Source: pRow.SourceAddress,
			Target: pRow.TargetAddress,
		};
		let tmpConfig = null;
		try { tmpConfig = pRow.SourceConfigJSON ? JSON.parse(pRow.SourceConfigJSON) : null; }
		catch (pError) { tmpConfig = null; }
		if (tmpConfig && tmpConfig.OperationConfiguration)
		{
			tmpBody.OperationConfiguration = tmpConfig.OperationConfiguration;
		}
		else if (tmpConfig && (tmpConfig.Projection || tmpConfig.Entity || tmpConfig.GroupBy || tmpConfig.ArrayPath))
		{
			tmpBody.OperationConfiguration = tmpConfig;
		}
		// POST /1.0/Projection requires OperationConfiguration; if we can't recover it, signal failure.
		return tmpBody.OperationConfiguration ? tmpBody : null;
	}

	_afterRunResult(pResponse, pLabel)
	{
		const tmpOK = pResponse && (pResponse.Success === true || /complete/i.test(String(pResponse.Status || '')));
		const tmpRead = (pResponse && pResponse.RowsRead != null) ? pResponse.RowsRead : '?';
		const tmpWritten = (pResponse && pResponse.RowsWritten != null) ? pResponse.RowsWritten : '?';
		if (tmpOK && tmpWritten === 0)
		{
			this._setActionResult('is-warning', 'fa-circle-exclamation', `${pLabel} completed but wrote 0 rows — check the source filter.`);
		}
		else if (tmpOK)
		{
			this._setActionResult('is-success', 'fa-circle-check', `${pLabel} complete — ${tmpRead} read, ${tmpWritten} written.`);
		}
		else
		{
			const tmpFailure = pResponse && pResponse.Failure;
			this._setActionResult('is-danger', 'fa-triangle-exclamation', `${pLabel} failed${(tmpFailure && tmpFailure.Reason) ? ': ' + tmpFailure.Reason : '.'}`);
		}
		const tmpState = this.pict.AppData.DatalakeDataset;
		tmpState.Begin = 0;
		this._reloadRowOnly();
		this._loadCount();
		this._loadRowsPage();
	}

	/** Re-read just the registry row + re-derive header/meta/actions (preserves the action-result banner). */
	_reloadRowOnly()
	{
		const tmpState = this.pict.AppData.DatalakeDataset;
		const tmpGUID = (tmpState.Row || {}).GUIDMaterializedProjection;
		if (!tmpGUID)
		{
			return;
		}
		this.restClient.getJSON(`MaterializedProjections/FilteredTo/FBV~GUIDMaterializedProjection~EQ~${encodeURIComponent(tmpGUID)}/0/1`, (pError, pBody) =>
		{
			if (!pError && Array.isArray(pBody) && pBody.length > 0)
			{
				tmpState.Row = pBody[0];
				tmpState.Kind = libCatalog.classifyKind(tmpState.Row);
				tmpState.IsImported = (tmpState.Kind === 'imported');
				tmpState.IsComputed = (tmpState.Kind === 'computed');
				this._deriveHeaderAndMeta();
				this._deriveActions();
				this.render();
			}
		});
	}

	_setBusy(pBusy)
	{
		const tmpState = this.pict.AppData.DatalakeDataset;
		tmpState.ActionBusy = !!pBusy;
		tmpState.ActionBusySlot = pBusy ? [ {} ] : [];
		tmpState.RefreshDisabledAttr = pBusy ? 'disabled' : '';
		this.render();
	}

	_setActionResult(pClass, pIcon, pMessage)
	{
		this.pict.AppData.DatalakeDataset.ActionResultSlot = [ { Class: pClass, Icon: pIcon, Message: pMessage } ];
		this.render();
	}

	deriveRenderState()
	{
		if (!this.pict.AppData.DatalakeDataset)
		{
			this.initializeState();
		}
	}

	// --- pager interactions ---

	nextPage()
	{
		const tmpState = this.pict.AppData.DatalakeDataset;
		if ((tmpState.Begin + PAGE_SIZE) >= tmpState.RowCount)
		{
			return;
		}
		tmpState.Begin += PAGE_SIZE;
		this._loadRowsPage();
	}

	prevPage()
	{
		const tmpState = this.pict.AppData.DatalakeDataset;
		if (tmpState.Begin <= 0)
		{
			return;
		}
		tmpState.Begin = Math.max(0, tmpState.Begin - PAGE_SIZE);
		this._loadRowsPage();
	}

	backToCatalog()
	{
		window.location.hash = '#/Datalake';
	}

	/** Projectionize a raw imported table: open the create wizard pre-filled to clone it into a queryable projection. */
	projectionizeThis()
	{
		const tmpRow = this.pict.AppData.DatalakeDataset.Row;
		if (tmpRow && tmpRow.PhysicalTableName && this.pict.providers.DatalakeProjectionEditorProvider)
		{
			this.pict.providers.DatalakeProjectionEditorProvider.newFromLakeTable(tmpRow.PhysicalTableName);
			window.location.hash = '#/Datalake/New';
		}
	}
}

module.exports = DatalakeDatasetProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'DatalakeDatasetProvider',
	AutoInitialize: true,
	AutoInitializeOrdinal: 0,
};

module.exports.cellText = cellText;
module.exports.columnsFromSchema = columnsFromSchema;
module.exports.PAGE_SIZE = PAGE_SIZE;
